import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useIsRTL } from "@/hooks";
import { getDeepSelectedCategory } from "@/utils";
import useSelectedByQuery from "../hooks/useSelectedByQuery";
import { FILTER_TYPES } from "../constants";
import { selectedFiltersContext } from ".";
import FilterWrapperElement from "./filterWrapperElement";
import FilterContainer from "./filtersContainer";
import { ActionButtons } from "./actionButtons";
import { Facet } from "../types";
import {
  arePlpFilterSelectionsEqualByParamKeys,
  buildFacetMapByIdentity,
  collectFilterQueryParamKeysFromFacets,
  getFacetIdentity,
  normalizeSelectedOptionsByQueryParam
} from "../utils";
import "./style.scss";
import FiltersTypeSection from "./filtersTypeSection";

const TABLET_WIDTH = 812;

const FilterSidebar: FC<{
  filters: Facet[];
  isFiltersOpen: boolean;
  onFiltersClose: (opts?: { revertUnsavedToUrl?: boolean }) => void;
}> = ({ filters, isFiltersOpen, onFiltersClose }) => {
  const [toggledFilterId, setToggledFilterId] = useState<string | null>(null);
  const [pendingTargetId, setPendingTargetId] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const isRTL = useIsRTL();
  const selectedByQuery = useSelectedByQuery();
  const { t } = useTranslation("productListing");
  const {
    onSelection,
    setSelectedFilters,
    selectedFilters,
    hasMobFilterSheetEdits = false
  } = useContext(selectedFiltersContext);

  const applyStartFiltersRef = useRef<Facet[] | null>(null);
  const applyStartQuerySnapshotRef = useRef<Record<string, string[]> | null>(
    null
  );

  const filterById = useMemo(() => {
    const map = buildFacetMapByIdentity<Facet>(filters);
    const moreFiltersContainer = (filters as any[]).find(
      (f: any) => f.type === FILTER_TYPES.MORE_FILTERS
    );
    (moreFiltersContainer?.data || []).forEach((item: any) => {
      map.set(getFacetIdentity(item), item as Facet);
    });
    return map;
  }, [filters]);

  const toggledFilter = useMemo(() => {
    if (toggledFilterId) {
      return filterById.get(toggledFilterId) ?? filters?.[0] ?? null;
    }
    return filters?.[0] ?? null;
  }, [toggledFilterId, filterById, filters]);

  const setToggledFilter = useCallback((filter: any) => {
    if (!filter || isEmpty(filter)) {
      setToggledFilterId(null);
    } else {
      setToggledFilterId(getFacetIdentity(filter));
    }
  }, []);

  const filterQueryParamKeys = useMemo(
    () => collectFilterQueryParamKeysFromFacets(filters),
    [filters]
  );

  const hasPendingChanges = hasMobFilterSheetEdits;

  const selectedByQueryNorm = useMemo(
    () => normalizeSelectedOptionsByQueryParam(selectedByQuery, ["q"]),
    [selectedByQuery]
  );

  // Switch the pending tab once the URL has changed but before new API filter data arrives,
  // so the old filterById still contains the target filter.
  useEffect(() => {
    if (!isApplying || !pendingTargetId) return;
    const queryChangedSinceApplyStart =
      !!applyStartQuerySnapshotRef.current &&
      !isEqual(selectedByQueryNorm, applyStartQuerySnapshotRef.current);
    const filtersPropChangedSinceApplyStart =
      !!applyStartFiltersRef.current &&
      applyStartFiltersRef.current !== filters;

    if (!queryChangedSinceApplyStart || filtersPropChangedSinceApplyStart) {
      return;
    }

    const next = filterById.get(pendingTargetId);
    if (next) {
      setToggledFilter(next);
    }
    setPendingTargetId(null);
    setIsApplying(false);
    applyStartFiltersRef.current = null;
    applyStartQuerySnapshotRef.current = null;
  }, [isApplying, pendingTargetId, selectedByQueryNorm, filterById, filters]);

  useEffect(() => {
    if (!isFiltersOpen) {
      setToggledFilterId(null);
      setPendingTargetId(null);
      setIsApplying(false);
      applyStartFiltersRef.current = null;
      applyStartQuerySnapshotRef.current = null;
    }
  }, [isFiltersOpen]);

  useEffect(() => {
    if (!filters.length && !isFiltersOpen) {
      setSelectedFilters({});
    }
  }, [filters, isFiltersOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > TABLET_WIDTH) {
        onFiltersClose();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const closeFilter = (revertUnsavedToUrl = true) => {
    onFiltersClose(
      revertUnsavedToUrl ? undefined : { revertUnsavedToUrl: false }
    );
  };

  const clearAllSelectedFilters = () => {
    if (!isEmpty(selectedFilters) || !isEmpty(selectedByQuery)) {
      const clearedFilters = Object.keys(selectedFilters).map(item => {
        return { queryParam: item, selectedOptions: [] };
      });
      onSelection(clearedFilters);
      setSelectedFilters({});
    }
  };

  const categoryFilter = useMemo(
    () => filters.find(f => f.type === FILTER_TYPES.CATEGORY) ?? null,
    [filters]
  );

  const selectedCategoryCount = useMemo(
    () =>
      categoryFilter
        ? getDeepSelectedCategory(categoryFilter as any).length
        : 0,
    [categoryFilter]
  );

  const onFilterTitleClick = (nextFilter: Facet) => {
    const switchingAway =
      toggledFilter &&
      getFacetIdentity(toggledFilter) !== getFacetIdentity(nextFilter);
    const leavingCategory =
      switchingAway && toggledFilter.type === FILTER_TYPES.CATEGORY;

    if (leavingCategory) {
      setPendingTargetId(null);
      setIsApplying(false);
      applyStartFiltersRef.current = null;
      applyStartQuerySnapshotRef.current = null;
      setToggledFilter(nextFilter);
      return;
    }

    if (!hasPendingChanges) {
      setPendingTargetId(null);
      setToggledFilter(nextFilter);
      return;
    }

    if (
      arePlpFilterSelectionsEqualByParamKeys(
        selectedFilters,
        selectedByQuery,
        filterQueryParamKeys
      )
    ) {
      setPendingTargetId(null);
      setToggledFilter(nextFilter);
      return;
    }

    const prevSearch = window.location.search;
    setPendingTargetId(getFacetIdentity(nextFilter));
    setIsApplying(true);
    applyStartFiltersRef.current = filters;
    applyStartQuerySnapshotRef.current = selectedByQueryNorm;
    Promise.resolve(onSelection()).then(() => {
      if (window.location.search === prevSearch) {
        setPendingTargetId(null);
        setIsApplying(false);
        applyStartFiltersRef.current = null;
        applyStartQuerySnapshotRef.current = null;
        setToggledFilter(nextFilter);
      }
    });
  };

  const onSelectCategory = () => {
    if (categoryFilter) onFilterTitleClick(categoryFilter);
  };

  const onApplyAndClose = () => {
    setPendingTargetId(null);
    setIsApplying(false);
    applyStartFiltersRef.current = null;
    applyStartQuerySnapshotRef.current = null;

    onSelection();
    closeFilter(false);
  };

  return (
    <div className="sidebar_container">
      <div
        className={cn("sidebar", {
          open: isFiltersOpen
        })}
        style={{ width: "100%" }}
      >
        <div className="sidebar_content">
          <FilterWrapperElement
            iconClassName="close"
            onIconClick={() => closeFilter()}
            filterTitle={t("filterTitle")}
            onClearBtnClick={clearAllSelectedFilters}
            isHeaderVisible={true}
          >
            <div className="mobile_filter_sheet">
              <div className={cn("filter_wrapper", { arabic: isRTL })}>
                <FiltersTypeSection
                  filters={filters}
                  selectedFilters={selectedFilters}
                  toggledFilter={toggledFilter}
                  setToggledFilter={setToggledFilter}
                  onFilterClick={onFilterTitleClick}
                />
                <div className="filter_content_section">
                  {toggledFilter && (
                    <ErrorBoundary fallback={null}>
                      <FilterContainer
                        filter={toggledFilter}
                        setToggledFilter={setToggledFilter}
                        toggledFilter={toggledFilter}
                        onApplyButtonClick={onSelection}
                        onSelectCategory={onSelectCategory}
                        selectedCategoryCount={selectedCategoryCount}
                      />
                    </ErrorBoundary>
                  )}
                </div>
              </div>
              <ActionButtons
                onCloseButtonClick={() => closeFilter()}
                onApplyButtonClick={onApplyAndClose}
              />
            </div>
          </FilterWrapperElement>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
